import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Sparkles, Plus, Wand2, Image, Film, Bell, ZoomIn, ChevronDown, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { setWallpapers } from '@/lib/collections/wallpapers';
import { Time, Address } from '@/lib/db-client';
import { ADMIN_ADDRESS } from '@/lib/constants';
import { useAuth, getIdToken } from '@pooflabs/web';
import { AuthContextType } from '@/components/types';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { loadApiKey } from '@/utils/adminApiKey';
import { CategoryNewButton } from '@/components/CategoryNewButton';

type MediaType = 'wallpaper' | 'live-wallpaper' | 'ringtone' | 'icon';

const MEDIA_TYPE_OPTIONS: { value: MediaType; label: string; color: string; icon: React.ReactNode }[] = [
  { value: 'wallpaper', label: 'Wallpaper', color: '#a78bfa', icon: <Image size={11} /> },
  { value: 'live-wallpaper', label: 'Live', color: '#a78bfa', icon: <Film size={11} /> },
  { value: 'ringtone', label: 'Ringtone', color: '#a78bfa', icon: <Bell size={11} /> },
  { value: 'icon', label: 'Icon', color: '#a78bfa', icon: <Sparkles size={11} /> },
];

const BASE_WALLPAPER_CATEGORIES = ['Nature', 'Abstract', 'Minimal', 'Dark', 'Gradient', 'Space', 'Urban', 'Cyberpunk'];

const GENERATION_MESSAGES = [
  'Painting pixels with AI...',
  'Dreaming your wallpaper into existence...',
  'Processing photons...',
  'Rendering visual poetry...',
  'Almost there — adding final details...',
  'Composing the perfect image...',
];

interface AdminGenerateWallpaperProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToAdmin?: () => void;
  existingCategories?: string[];
  existingLiveWallpaperCategories?: string[];
  existingRingtoneCategories?: string[];
  initialMediaType?: MediaType;
  onNewCategory?: (mediaType: 'wallpaper' | 'live-wallpaper' | 'ringtone', category: string) => void;
}

export const AdminGenerateWallpaper: React.FC<AdminGenerateWallpaperProps> = ({
  isOpen,
  onClose,
  onNavigateToAdmin,
  existingCategories,
  existingLiveWallpaperCategories,
  existingRingtoneCategories,
  initialMediaType,
  onNewCategory,
}) => {
  const { user } = useAuth() as AuthContextType;
  const [prompt, setPrompt] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState<number | ''>('');
  const [mediaType, setMediaType] = useState<MediaType>(initialMediaType ?? 'wallpaper');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState('');
  const [generationMsgIdx, setGenerationMsgIdx] = useState(0);
  const [apiKey, setApiKey] = useState('');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [customCategories, setCustomCategories] = useState<string[]>([]);

  // Load API key from database when modal opens
  useEffect(() => {
    if (isOpen) {
      loadApiKey().then(key => setApiKey(key));
      if (initialMediaType) setMediaType(initialMediaType);
    }
  }, [isOpen, initialMediaType]);

  // When switching to live-wallpaper/ringtone, auto-select first existing category if available
  useEffect(() => {
    if (mediaType === 'live-wallpaper') {
      const merged = [...new Set([...(existingLiveWallpaperCategories ?? []), ...customCategories])];
      if (merged.length > 0) setCategory(merged[0]);
      else setCategory('');
    } else if (mediaType === 'ringtone') {
      const merged = [...new Set([...(existingRingtoneCategories ?? []), ...customCategories])];
      if (merged.length > 0) setCategory(merged[0]);
      else setCategory('');
    }
  }, [mediaType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build category list based on media type, merging persistent + session-local custom categories
  const allCategories = mediaType === 'live-wallpaper'
    ? [...new Set([...(existingLiveWallpaperCategories ?? []), ...customCategories])]
    : mediaType === 'ringtone'
    ? [...new Set([...(existingRingtoneCategories ?? []), ...customCategories])]
    : mediaType === 'icon'
    ? [...new Set([...customCategories])]
    : [...new Set([
        ...BASE_WALLPAPER_CATEGORIES,
        ...(existingCategories ?? []),
        ...customCategories,
      ])];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe the wallpaper you want');
      return;
    }

    if (!apiKey?.trim()) {
      toast.error('Enter your API key in the Add Wallpaper panel');
      return;
    }

    setGenerating(true);
    setGeneratedImageUrl('');
    setGenerationMsgIdx(0);

    const msgInterval = setInterval(() => {
      setGenerationMsgIdx(prev => (prev + 1) % GENERATION_MESSAGES.length);
    }, 4000);

    try {
      const token = await getIdToken();
      if (!token || !user?.address) {
        toast.error('Authentication required');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, user.address);
      const result = await authApi.post('/api/generate-wallpaper', {
        prompt: prompt.trim(),
        aspectRatio: '9:16',
        apiKey: apiKey.trim(),
      }) as { imageUrl: string; replicateUrl?: string };

      setGeneratedImageUrl(result.imageUrl);

      if (!title) {
        const words = prompt.trim().split(' ').slice(0, 3).map(w => w.charAt(0).toUpperCase() + w.slice(1));
        setTitle(words.join(' '));
      }

    } catch (err) {
      console.error('Generation error:', err);
      toast.error('Generation failed — please try again');
    } finally {
      clearInterval(msgInterval);
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!generatedImageUrl) { toast.error('Generate an image first'); return; }
    if (!category) { toast.error('Category is required'); return; }
    if (price === '' || !(price > 0)) { toast.error('Price must be greater than 0'); return; }

    // Persist new category if user typed one that doesn't exist in the list
    if (category && !allCategories.includes(category)) {
      setCustomCategories(prev => prev.includes(category) ? prev : [...prev, category]);
      if (mediaType === 'wallpaper' || mediaType === 'live-wallpaper' || mediaType === 'ringtone') {
        onNewCategory?.(mediaType, category);
      }
    }

    setSaving(true);
    try {
      const wallpaperId = `wall-${Date.now()}`;
      const ok = await setWallpapers(wallpaperId, {
        title: title.trim(),
        imageUrl: generatedImageUrl,
        thumbnailUrl: generatedImageUrl,
        category,
        price: (price as number) * 1000000,
        uploadedBy: Address.publicKey(ADMIN_ADDRESS),
        createdAt: Time.Now,
        downloadCount: 0,
        mediaType,
        status: 'published',
      } as any);

      if (ok) {
        handleClose();
      } else {
        toast.error('Failed to save wallpaper');
      }
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Failed to save wallpaper');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (generating || saving) return;
    setPrompt('');
    setTitle('');
    setCategory('');
    setPrice('');
    setMediaType(initialMediaType ?? 'wallpaper');
    setGeneratedImageUrl('');
    setGenerationMsgIdx(0);
    setFullscreenOpen(false);
    setCustomCategories([]);
    onClose();
  };

  const isBusy = generating || saving;

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 3000,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 520,
              background: '#0a0a14',
              borderRadius: 20,
              border: '1px solid rgba(255,255,255,0.09)',
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
            }}
          >
            {/* ── Header ── */}
            <div style={{ padding: '14px 16px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Back arrow button */}
                <motion.button
                  whileHover={{ scale: 1.1, background: 'rgba(139,92,246,0.25)' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onNavigateToAdmin ?? handleClose}
                  disabled={isBusy}
                  title="Go back to Admin Panel"
                  style={{
                    width: 36, height: 36, flexShrink: 0,
                    borderRadius: 10,
                    background: 'rgba(139,92,246,0.12)',
                    border: '1px solid rgba(139,92,246,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isBusy ? 'not-allowed' : 'pointer',
                    color: '#a78bfa',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <ArrowLeft size={18} />
                </motion.button>

                {/* Title + subtitle */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: '#a78bfa',
                    fontFamily: "'Syne', sans-serif", lineHeight: 1.1,
                    letterSpacing: '-0.01em',
                  }}>
                    AI Generate
                  </div>
                  <div style={{
                    fontSize: 12, color: 'rgba(200,200,210,0.5)',
                    fontFamily: "'Syne', sans-serif", marginTop: 2,
                  }}>
                    Create wallpapers with AI
                  </div>
                </div>

                {/* Close button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleClose}
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: isBusy ? 'not-allowed' : 'pointer',
                      color: 'rgba(240,240,245,0.6)',
                    }}
                  >
                    <X size={14} />
                  </motion.button>
                </div>
              </div>

              {/* Divider */}
              <div style={{
                height: 1,
                background: 'rgba(255,255,255,0.08)',
                marginTop: 10, marginBottom: 0,
              }} />
            </div>

            {/* ── Content ── */}
            <div style={{ padding: '8px 16px 16px' }}>

              {/* Preview area */}
              <div style={{
                height: 150, borderRadius: 12,
                background: generatedImageUrl
                  ? 'transparent'
                  : 'rgba(168,85,247,0.04)',
                border: generatedImageUrl
                  ? '1px solid rgba(139,92,246,0.2)'
                  : '1px solid rgba(168,85,247,0.12)',
                marginBottom: 10, overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
                cursor: generatedImageUrl ? 'zoom-in' : 'default',
              }}
                onClick={() => { if (generatedImageUrl) setFullscreenOpen(true); }}
              >
                {generatedImageUrl ? (
                  <img
                    src={generatedImageUrl}
                    alt="Generated wallpaper"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }}
                  />
                ) : generating ? (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
                  }}>
                    <div style={{ position: 'relative', width: 48, height: 48 }}>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                        style={{
                          position: 'absolute', inset: 0,
                          borderRadius: '50%',
                          border: '2px solid transparent',
                          borderTopColor: '#c084fc',
                          borderRightColor: 'rgba(168,85,247,0.3)',
                        }}
                      />
                      <motion.div
                        animate={{ scale: [1, 1.15, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                        style={{
                          position: 'absolute', inset: 8,
                          borderRadius: '50%',
                          background: 'radial-gradient(circle, rgba(168,85,247,0.3) 0%, transparent 70%)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Sparkles size={16} color="#c084fc" />
                      </motion.div>
                    </div>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={generationMsgIdx}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.3 }}
                        style={{
                          fontSize: 11, color: 'rgba(192,132,252,0.7)',
                          fontFamily: "'Syne', sans-serif", fontWeight: 600,
                          textAlign: 'center',
                        }}
                      >
                        {GENERATION_MESSAGES[generationMsgIdx]}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                ) : (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  }}>
                    <Wand2 size={28} color="rgba(168,85,247,0.35)" />
                    <span style={{
                      fontSize: 12, color: 'rgba(240,240,245,0.3)',
                      fontFamily: "'Syne', sans-serif", fontWeight: 600,
                    }}>
                      Your generated wallpaper appears here
                    </span>
                  </div>
                )}

                {generatedImageUrl && (
                  <>
                    <div style={{
                      position: 'absolute', bottom: 8, right: 8,
                      padding: '3px 8px', borderRadius: 20,
                      background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
                      fontSize: 9, color: 'rgba(192,132,252,0.8)',
                      fontFamily: "'DM Mono', monospace",
                      border: '1px solid rgba(168,85,247,0.2)',
                    }}>
                      ✨ AI Generated
                    </div>
                    <div style={{
                      position: 'absolute', top: 8, right: 8,
                      width: 28, height: 28, borderRadius: 8,
                      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <ZoomIn size={14} color="rgba(255,255,255,0.8)" />
                    </div>
                  </>
                )}
              </div>

              {/* Inner content card */}
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 14,
                padding: '10px 12px 12px',
              }}>
                {/* Prompt + Title — side by side */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  {/* Prompt — left half */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      height: 20, marginBottom: 3,
                    }}>
                      <label style={{
                        fontSize: 9, fontWeight: 700,
                        color: 'rgba(167,139,250,0.5)',
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        fontFamily: "'Syne', sans-serif",
                      }}>
                        Prompt
                      </label>
                    </div>
                    <textarea
                      value={prompt}
                      onChange={e => setPrompt(e.target.value)}
                      placeholder="Describe the wallpaper..."
                      rows={3}
                      disabled={generating}
                      style={{
                        width: '100%', padding: '6px 8px',
                        borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)',
                        background: 'rgba(255,255,255,0.04)', color: '#f0f0f5',
                        fontSize: 11, outline: 'none', boxSizing: 'border-box',
                        fontFamily: "'Syne', sans-serif", resize: 'none',
                        lineHeight: 1.55,
                        opacity: generating ? 0.6 : 1,
                      }}
                    />
                  </div>

                  {/* Title — right half */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between',
                      height: 20, marginBottom: 3,
                    }}>
                      <label style={{
                        fontSize: 9, fontWeight: 700,
                        color: 'rgba(167,139,250,0.7)',
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        fontFamily: "'Syne', sans-serif",
                      }}>
                        Title *
                      </label>
                      {/* AI suggest title from prompt */}
                      <motion.button
                        whileHover={(!prompt.trim() || generating) ? {} : { scale: 1.05 }}
                        whileTap={(!prompt.trim() || generating) ? {} : { scale: 0.95 }}
                        onClick={() => {
                          if (!prompt.trim()) return;
                          const words = prompt.trim().split(' ').slice(0, 3).map(w => w.charAt(0).toUpperCase() + w.slice(1));
                          setTitle(words.join(' '));
                        }}
                        disabled={!prompt.trim() || generating}
                        title={!prompt.trim() ? 'Enter a prompt first' : 'Suggest title from prompt'}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 3,
                          padding: '2px 6px', borderRadius: 5,
                          background: (!prompt.trim() || generating)
                            ? 'rgba(20,15,40,0.6)'
                            : 'rgba(30,20,60,0.8)',
                          border: (!prompt.trim() || generating)
                            ? '1px solid rgba(139,92,246,0.2)'
                            : '1px solid rgba(139,92,246,0.4)',
                          color: (!prompt.trim() || generating)
                            ? 'rgba(240,240,245,0.35)'
                            : 'white',
                          fontSize: 9,
                          cursor: (!prompt.trim() || generating) ? 'not-allowed' : 'pointer',
                          fontFamily: "'Syne', sans-serif",
                          transition: 'all 0.15s ease',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span style={{ color: (!prompt.trim() || generating) ? 'rgba(234,179,8,0.4)' : '#FFD700', fontSize: 10 }}>✦</span>
                        AI
                      </motion.button>
                    </div>
                    <input
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="Cosmic Dawn"
                      style={{
                        width: '100%', padding: '6px 8px',
                        borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                        background: 'rgba(255,255,255,0.05)', color: '#f0f0f5',
                        fontSize: 11, outline: 'none', boxSizing: 'border-box',
                        fontFamily: "'Syne', sans-serif",
                      }}
                    />
                  </div>
                </div>

                {/* Category + Price */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: 2, minWidth: 0 }}>
                    <label style={{
                      display: 'block', fontSize: 9, fontWeight: 700,
                      color: 'rgba(167,139,250,0.7)',
                      textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3,
                      fontFamily: "'Syne', sans-serif",
                    }}>
                      Category
                    </label>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <select
                          value={allCategories.includes(category) ? category : (category ? '__custom__' : '')}
                          onChange={e => {
                            if (e.target.value !== '__custom__') setCategory(e.target.value);
                          }}
                          disabled={generating}
                          style={{
                            width: '100%', padding: '6px 24px 6px 8px',
                            borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                            background: 'rgba(255,255,255,0.05)', color: '#f0f0f5',
                            fontSize: 11, outline: 'none', boxSizing: 'border-box',
                            fontFamily: "'Syne', sans-serif",
                            cursor: generating ? 'not-allowed' : 'pointer',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            opacity: generating ? 0.6 : 1,
                          } as React.CSSProperties}
                        >
                          {allCategories.length === 0 && (
                            <option value="" disabled>No categories yet</option>
                          )}
                          {allCategories.map(cat => (
                            <option key={cat} value={cat} style={{ background: '#0a0a14', color: '#f0f0f5' }}>
                              {cat}
                            </option>
                          ))}
                          {category && !allCategories.includes(category) && (
                            <option value="__custom__" style={{ background: '#0a0a14', color: '#a78bfa' }}>
                              {category}
                            </option>
                          )}
                        </select>
                        <div style={{
                          position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
                          pointerEvents: 'none', color: 'rgba(167,139,250,0.5)',
                        }}>
                          <ChevronDown size={11} />
                        </div>
                      </div>
                      <CategoryNewButton
                        disabled={generating}
                        onAdd={cat => {
                          setCustomCategories(prev => prev.includes(cat) ? prev : [...prev, cat]);
                          if (mediaType === 'wallpaper' || mediaType === 'live-wallpaper' || mediaType === 'ringtone') {
                            onNewCategory?.(mediaType, cat);
                          }
                          setCategory(cat);
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <label style={{
                      display: 'block', fontSize: 9, fontWeight: 700,
                      color: 'rgba(167,139,250,0.7)',
                      textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3,
                      fontFamily: "'Syne', sans-serif",
                    }}>
                      Price
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={price}
                      onChange={e => {
                        const raw = e.target.value;
                        if (raw === '') { setPrice(''); return; }
                        const v = parseFloat(raw);
                        if (!Number.isNaN(v) && v >= 0) setPrice(v);
                      }}
                      placeholder="0.00"
                      disabled={generating}
                      style={{
                        width: '100%',
                        background: 'rgba(139,92,246,0.12)',
                        border: '1px solid rgba(139,92,246,0.35)',
                        color: '#f0f0f5',
                        fontSize: 12, fontWeight: 700,
                        fontFamily: "'DM Mono', monospace",
                        textAlign: 'center', borderRadius: 8, padding: '6px 8px',
                        WebkitAppearance: 'none', boxSizing: 'border-box', outline: 'none',
                        opacity: generating ? 0.6 : 1,
                      } as React.CSSProperties}
                    />
                  </div>
                </div>

                {/* Generate button */}
                <motion.button
                  whileHover={generating ? {} : { scale: 1.02, boxShadow: '0 0 18px rgba(168,85,247,0.35)' }}
                  whileTap={generating ? {} : { scale: 0.98 }}
                  onClick={handleGenerate}
                  disabled={generating}
                  style={{
                    width: '100%', padding: '8px 12px',
                    borderRadius: 10,
                    background: generating
                      ? 'rgba(168,85,247,0.35)'
                      : 'rgba(139,92,246,0.85)',
                    border: '1px solid rgba(167,139,250,0.45)',
                    color: '#fff', fontSize: 13, fontWeight: 700,
                    cursor: generating ? 'not-allowed' : 'pointer',
                    fontFamily: "'Syne', sans-serif",
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    opacity: generating ? 0.75 : 1,
                    transition: 'all 0.15s ease',
                    marginBottom: generatedImageUrl ? 8 : 0,
                  }}
                >
                  {generating ? (
                    <>
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} />
                      {generatedImageUrl ? 'Regenerate' : 'Generate Wallpaper'}
                    </>
                  )}
                </motion.button>

                {/* Post-generation save section */}
                {generatedImageUrl && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Divider */}
                    <div style={{
                      height: 1, background: 'rgba(255,255,255,0.08)',
                      margin: '4px 0 8px',
                    }} />

                    {/* Section label */}
                    <div style={{
                      fontSize: 9, fontWeight: 700,
                      color: 'rgba(167,139,250,0.5)',
                      textTransform: 'uppercase', letterSpacing: '0.1em',
                      fontFamily: "'Syne', sans-serif",
                      marginBottom: 8,
                    }}>
                      Save to Catalog
                    </div>

                    {/* Media Type selector — hidden when type is pre-determined */}
                    {!initialMediaType && (
                      <div style={{ marginBottom: 8 }}>
                        <label style={{
                          display: 'block', fontSize: 9, fontWeight: 700,
                          color: 'rgba(167,139,250,0.7)',
                          textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3,
                          fontFamily: "'Syne', sans-serif",
                        }}>
                          Type
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 5 }}>
                          {MEDIA_TYPE_OPTIONS.map(opt => {
                            const isActive = mediaType === opt.value;
                            return (
                              <motion.button
                                key={opt.value}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setMediaType(opt.value)}
                                style={{
                                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                                  padding: '6px 4px', borderRadius: 8,
                                  background: isActive ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.04)',
                                  border: isActive ? '1px solid rgba(167,139,250,0.45)' : '1px solid rgba(255,255,255,0.08)',
                                  color: isActive ? '#a78bfa' : 'rgba(240,240,245,0.5)',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <span style={{ color: isActive ? '#a78bfa' : 'rgba(240,240,245,0.35)', display: 'flex' }}>{opt.icon}</span>
                                <span style={{
                                  fontSize: 9, fontWeight: 700,
                                  fontFamily: "'Syne', sans-serif",
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {opt.label}
                                </span>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Save button row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <motion.button
                        whileHover={saving ? {} : { scale: 1.02, boxShadow: '0 0 18px rgba(139,92,246,0.4)' }}
                        whileTap={saving ? {} : { scale: 0.98 }}
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                          flex: 1,
                          padding: '8px 12px', borderRadius: 10,
                          background: saving ? 'rgba(139,92,246,0.4)' : 'rgba(139,92,246,0.85)',
                          border: '1px solid rgba(167,139,250,0.45)',
                          color: '#fff', fontSize: 13, fontWeight: 700,
                          cursor: saving ? 'not-allowed' : 'pointer',
                          fontFamily: "'Syne', sans-serif",
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                          opacity: saving ? 0.75 : 1,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {saving ? (
                          <>
                            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Plus size={14} />
                            Save to Catalog
                          </>
                        )}
                      </motion.button>

                      {/* Cancel text link */}
                      <button
                        onClick={handleClose}
                        disabled={isBusy}
                        style={{
                          background: 'none', border: 'none', padding: '8px 6px',
                          color: 'rgba(240,240,245,0.4)',
                          fontSize: 12, fontWeight: 600,
                          fontFamily: "'Syne', sans-serif",
                          cursor: isBusy ? 'not-allowed' : 'pointer',
                          transition: 'color 0.15s ease',
                          flexShrink: 0,
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(240,240,245,0.7)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(240,240,245,0.4)'; }}
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Fullscreen image preview overlay */}
    <AnimatePresence>
      {fullscreenOpen && generatedImageUrl && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => setFullscreenOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 4000,
            background: 'rgba(0,0,0,0.92)',
            backdropFilter: 'blur(20px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          {/* Close button */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={e => { e.stopPropagation(); setFullscreenOpen(false); }}
            style={{
              position: 'absolute', top: 20, right: 20,
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', zIndex: 1,
            }}
          >
            <X size={18} />
          </motion.button>

          {/* Image */}
          <motion.img
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            src={generatedImageUrl}
            alt="Generated wallpaper — fullscreen"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: '100%', maxHeight: '90vh',
              borderRadius: 16,
              boxShadow: '0 0 80px rgba(168,85,247,0.25)',
              objectFit: 'contain',
            }}
          />

          {/* Tap anywhere to close hint */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              position: 'absolute', bottom: 24,
              fontSize: 11, color: 'rgba(255,255,255,0.3)',
              fontFamily: "'Syne', sans-serif",
              letterSpacing: '0.06em',
            }}
          >
            Tap anywhere to close
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};
